import { Fragment, type ReactNode } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { DefinitionGrid, Field, designTokens, type DefinitionGridItem } from '../../../design-system';
import type { WorkflowRequest } from '../model';
import type { WorkflowPort } from '../port';
import type { EmployeeDirectorySnapshot } from '../../people/public';
import { WorkflowFileFieldControl } from '../WorkflowSpecialFieldControls';
import { WorkflowRichTextViewer } from '../WorkflowRichTextEditor';
import { formatWorkflowFieldValue } from '../models/workflowActivityPresentation';
import { workflowRequestTitle } from '../models/workflowRequestPageModel';

type Props = {
  request: WorkflowRequest;
  port: WorkflowPort;
  fixtureMode: boolean;
  directory?: EmployeeDirectorySnapshot;
  directoryLoading?: boolean;
  onOpenReference?: (id: string) => void;
  columns?: 2 | 3 | 4;
};

/** 제출 문서는 입력 control 대신 정적 값으로 읽고, 첨부의 권한·보안 검사는 기존 control이 소유합니다. */
export function WorkflowRequestReadOnlyFields({ request, port, fixtureMode, directory, directoryLoading, onOpenReference, columns = 2 }: Props) {
  const context = { fields: request.fields, people: directory?.members, organizations: directory?.organizations };
  const sections: ReactNode[] = [];
  let items: DefinitionGridItem[] = [];
  const flush = () => {
    if (!items.length) return;
    sections.push(<DefinitionGrid key={`properties-${sections.length}`} items={items} columns={columns} />);
    items = [];
  };
  for (const field of request.fields ?? []) {
    const value = request.values?.[field.key];
    if (field.type === 'LONG_TEXT' || field.type === 'FILE' || field.type === 'DISPLAY_TEXT') {
      flush();
      if (field.type === 'FILE') {
        sections.push(<Field.Root key={field.key} readOnly>
          <Field.Header><Field.Label>{field.label}</Field.Label></Field.Header>
          <Field.Control>{(state) => <WorkflowFileFieldControl
            scopeKey={request.id}
            field={field} value={value} disabled state={state} port={port} fixtureMode={fixtureMode}
            ensureDraft={() => Promise.resolve(request.id)} onChange={() => undefined}
          />}</Field.Control>
        </Field.Root>);
      } else if (field.type === 'DISPLAY_TEXT') {
        sections.push(<Box key={field.key} role="note">
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{field.label}</Typography>
          {field.helpText ? <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>{field.helpText}</Typography> : null}
        </Box>);
      } else {
        sections.push(<Box key={field.key} component="section" aria-label={field.label}>
          <Typography component="h3" variant="body2" sx={{ fontWeight: 600, mb: 1.5 }}>{field.label}</Typography>
          <WorkflowRichTextViewer value={typeof value === 'string' ? value : ''} emptyText="미입력" sx={{ color: designTokens.color.primaryText, overflowWrap: 'anywhere' }} />
        </Box>);
      }
      continue;
    }
    let display: ReactNode;
    if (field.type === 'PERSON' || field.type === 'ORGANIZATION') {
      const name = field.type === 'PERSON'
        ? directory?.members.find((member) => member.personId === value)?.name
        : directory?.organizations.find((organization) => organization.id === value)?.name;
      display = value ? name || (directoryLoading ? '이름 확인 중' : '이름을 확인할 수 없습니다.') : '미입력';
    } else if (field.type === 'WORKFLOW_REFERENCE') {
      const values = Array.isArray(value) ? value : value && typeof value === 'object' ? [value] : [];
      const titles = values.map((item: unknown) => item && typeof item === 'object'
        ? workflowRequestTitle({ title: 'title' in item && typeof item.title === 'string' ? item.title : '', templateName: 'templateName' in item && typeof item.templateName === 'string' ? item.templateName : '' })
        : '문서 이름을 확인할 수 없습니다.');
      display = titles.length ? <Stack spacing={0.5}>{titles.map((title, index) => {
        const reference = values[index];
        const id = reference && typeof reference === 'object' && 'id' in reference && typeof reference.id === 'string' ? reference.id : undefined;
        return id && onOpenReference ? <Button key={id} size="small" onClick={() => onOpenReference(id)} sx={{ alignSelf: 'flex-start', textAlign: 'left', whiteSpace: 'normal' }}>{title} 열기</Button> : <Typography key={index} variant="body2">{title}</Typography>;
      })}</Stack> : '미입력';
    } else {
      display = <Box component="span" sx={{ whiteSpace: 'pre-wrap' }}>{value == null || value === '' || (Array.isArray(value) && !value.length) ? '미입력' : formatWorkflowFieldValue(field, value, context)}</Box>;
    }
    items.push({ id: field.key, label: field.label, value: display, wide: field.type === 'WORKFLOW_REFERENCE' });
  }
  flush();
  return <Stack spacing={3}>
    {sections.length ? sections.map((section, index) => <Fragment key={index}>{section}</Fragment>) : <Typography variant="body2" color="text.secondary">작성된 요청 정보가 없습니다.</Typography>}
  </Stack>;
}
